import { request } from "graphql-request";
import { nanoid } from "nanoid";
import { config } from "@/utils/config";
import {
  ShopifyProduct,
  CartItem,
  ShopifyCart,
  CartCreateResponse,
  Cart,
  Property,
  Kiosk,
} from "@/types";

const GET_PRODUCTS_QUERY = `
  query getProducts($collectionId: ID!, $first: Int!) {
    collection(id: $collectionId) {
      products(first: $first) {
        edges {
          node {
            id
            title
            handle
            images(first: 1) {
              edges {
                node {
                  id
                  url
                  altText
                }
              }
            }
            variants(first: 10) {
              edges {
                node {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  availableForSale
                }
              }
            }
            requiresSellingPlan
            sellingPlanGroups(first: 5) {
              edges {
                node {
                  name
                  sellingPlans(first: 10) {
                    edges {
                      node {
                        id
                        name
                        description
                        options {
                          name
                          value
                        }
                        priceAdjustments {
                          adjustmentValue {
                            ... on SellingPlanPercentagePriceAdjustment {
                              adjustmentPercentage
                            }
                            ... on SellingPlanFixedAmountPriceAdjustment {
                              adjustmentAmount {
                                amount
                                currencyCode
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
          }
        }
      }
    }
  }
`;

const CART_CREATE_MUTATION = `
  mutation cartCreate($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        id
        checkoutUrl
        attributes {
          key
          value
        }
        lines(first: 10) {
          edges {
            node {
              id
              quantity
              merchandise {
                ... on ProductVariant {
                  id
                  title
                  price {
                    amount
                    currencyCode
                  }
                  product {
                    id
                    title
                    images(first: 1) {
                      edges {
                        node {
                          url
                          altText
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        cost {
          totalAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

export class ShopifyService {
  private static instance: ShopifyService;
  private endpoint: string;
  private headers: Record<string, string>;

  private constructor() {
    this.endpoint = config.shopify.graphqlEndpoint;
    this.headers = {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": config.shopify.storefrontToken,
    };
  }

  public static getInstance(): ShopifyService {
    if (!ShopifyService.instance) {
      ShopifyService.instance = new ShopifyService();
    }
    return ShopifyService.instance;
  }

  async getProducts(collectionId?: string): Promise<ShopifyProduct[]> {
    try {
      // Use provided collectionId or fallback to config
      const effectiveCollectionId = collectionId || config.shopify.collectionId;

      if (!effectiveCollectionId) {
        throw new Error("Collection ID is required");
      }

      const variables = {
        collectionId: `gid://shopify/Collection/${effectiveCollectionId}`,
        first: 250, // Adjust as needed
      };

      const response = (await request(
        this.endpoint,
        GET_PRODUCTS_QUERY,
        variables,
        this.headers
      )) as {
        collection?: {
          products?: {
            edges?: Array<{ node: ShopifyProduct }>;
          };
        };
      };

      const products =
        response.collection?.products?.edges?.map((edge) => edge.node) || [];

      return products;
    } catch (error) {
      console.error("❌ Error fetching products:", error);
      throw new Error("Failed to fetch products from Shopify");
    }
  }

  async createCart(
    items: Array<{
      variantId: string;
      quantity: number;
      sellingPlanId?: string;
    }>,
    property: Property | null,
    kiosk: Kiosk | null
  ): Promise<{ cart: ShopifyCart; sessionId: string }> {
    try {
      const sessionId = nanoid();

      const cartLines = items.map((item) => ({
        merchandiseId: item.variantId,
        quantity: item.quantity,
        ...(item.sellingPlanId && { sellingPlanId: item.sellingPlanId }),
      }));
      const buyerIdentity = property?.address
        ? {
            deliveryAddressPreferences: [
              {
                deliveryAddress: {
                  address1: property.address.address1,
                  address2: property.address.address2,
                  city: property.address.city,
                  province: property.address.province,
                  country: property.address.country,
                  zip: property.address.zip,
                  firstName: property.address?.first_name,
                  lastName: property.address?.last_name,
                  phone: property.address?.phone || "",
                },
                oneTimeUse: true,
              },
            ],
          }
        : undefined;

      const input = {
        lines: cartLines,
        attributes: [
          {
            key: "Property",
            value: property?.name || "Unknown",
          },
          {
            key: "Kiosk",
            value: kiosk?.name || "Unknown",
          },
          {
            key: "Channel",
            value: "Donation",
          },
          {
            key: "SessionId",
            value: sessionId,
          },
        ],
        buyerIdentity,
        discountCodes: ["TEMPLESHIP"],
      };

      const response = (await request(
        this.endpoint,
        CART_CREATE_MUTATION,
        { input },
        this.headers
      )) as { cartCreate: CartCreateResponse };

      if (response.cartCreate.userErrors.length > 0) {
        throw new Error(
          `Cart creation failed: ${response.cartCreate.userErrors
            .map((error) => error.message)
            .join(", ")}`
        );
      }

      if (!response.cartCreate.cart) {
        throw new Error("Cart creation failed: No cart returned");
      }
      return { cart: response.cartCreate.cart, sessionId };
    } catch (error) {
      console.error("❌ Error creating cart:", error);
      throw new Error("Failed to create cart in Shopify");
    }
  }

  // Helper method to convert Shopify cart to our Cart format
  convertShopifyCartToCart(shopifyCart: ShopifyCart): Cart {
    const items: CartItem[] = shopifyCart.lines.edges.map((edge) => {
      const line = edge.node;
      const variant = line.merchandise;
      const product = variant.product;

      return {
        productId: product.id,
        variantId: variant.id,
        title: product.title,
        variantTitle: variant.title,
        price: parseFloat(variant.price.amount),
        quantity: line.quantity,
        image: product.images.edges[0]?.node.url,
        lineId: line.id,
      };
    });

    return {
      items,
      totalQuantity: items.reduce((sum, item) => sum + item.quantity, 0),
      totalPrice: parseFloat(shopifyCart.cost.totalAmount.amount),
      id: shopifyCart.id,
      checkoutUrl: shopifyCart.checkoutUrl,
    };
  }
}
